import React from "react";
import { PilotPropertyCard } from "../../pages/PropertiesPage";
import { TransactionMomentum } from "../../components/dealroom/TransactionMomentum";
import { CriticalPathToExchange } from "../../components/dealroom/CriticalPathToExchange";
import { HeroParticipantsPanel } from "../../components/dealroom/HeroParticipantsPanel";

/* ─────────────────────────────────────────────────────────────
   MarketingDealRoom · Phase 2 Homepage
   ------------------------------------------------------------
   Zooms from the portfolio (MarketingProperties above) into ONE
   transaction — 23 Hallings Wharf. Composes REAL production
   Deal Room primitives with a static, prop-only demo payload:

     • PilotPropertyCard          — property/transaction identity
     • TransactionMomentum        — momentum signal
     • CriticalPathToExchange     — the operational path
     • HeroParticipantsPanel      — the 5 canonical participants

   All four are pure prop-driven components. No API calls, no
   chainStore mutation on mount, no auth dependency. The Deal
   Room narrative:

     23 Hallings Wharf needs attention.
     WHY?      Management pack overdue.
     WHAT?     Chase the management pack.
     WHO?      Buyer, seller, both solicitors, broker (visible).
     WHERE?    Enquiries — one step before Signed Contracts.

   Nothing invented. No unsupported claims. No fake certainty
   metrics.  Deal Room production files are UNTOUCHED.
   ───────────────────────────────────────────────────────────── */

const DAY = 24 * 60 * 60 * 1000;

const buildDeal = () => {
  const now = Date.now();
  return {
    id: "mkt-dr-01",
    stage: "enquiries",
    targetExchange: new Date(now + 9 * DAY).toISOString(),
    /* TransactionMomentum reads deal.activity to derive its label.
       We pass an empty activity feed and let the "blockers" branch
       fire off the 2 overdue tasks below → renders:
         "Multiple blockers detected · 2 blocking actions outstanding" */
    activity: [],
  };
};

/* Two overdue tasks → produces the "Multiple blockers detected"
   momentum label AND lights the enquiries step of the critical
   path as blocked (kickoff_stage_hint === "enquiries"). */
const buildOpenTasks = () => {
  const now = Date.now();
  return [
    {
      id: "mkt-t-01",
      title: "Chase management pack — freeholder",
      kickoff_stage_hint: "enquiries",
      due_at: new Date(now - 3 * DAY).toISOString(),
    },
    {
      id: "mkt-t-02",
      title: "Reply on outstanding enquiries — 3 items",
      kickoff_stage_hint: "enquiries",
      due_at: new Date(now - 1 * DAY).toISOString(),
    },
  ];
};

/* 5 canonical participants populated — no empty slots. Names use
   the same fields the production panel reads (full_name + email
   + participant_type). */
const PARTICIPANTS = [
  { participant_type: "seller",           full_name: "Jane Carter",     email: "jane.carter@example.co.uk" },
  { participant_type: "buyer",            full_name: "Sam Patel",       email: "s.patel@example.co.uk" },
  { participant_type: "seller_solicitor", full_name: "Hollis & Green",  email: "conveyancing@hollisgreen.co.uk" },
  { participant_type: "buyer_solicitor",  full_name: "Marlow Legal",    email: "team@marlowlegal.co.uk" },
  { participant_type: "mortgage_broker",  full_name: "Ivy Whitmore",    email: "iw@bridgemortgages.co.uk" },
];

const HERO_PROPERTY = {
  id: "mkt-dr-p-01",
  dealId: null, /* keeps CTA a button (Create Deal Room) instead of a Link — intercepted anyway */
  address: "23 Hallings Wharf, Stratford, London",
  postcode: "E15 2GX",
  type: "Apartment",
  beds: 2, baths: 2,
  price: "£645,000",
  status: "SALES AGREED",
  progression: "Enquiries · Management pack overdue",
  step: 3, stepTotal: 6,
  updated: "2h ago",
  exch: "Exch · 6 Mar",
  opState: "Needs attention",
  assignees: 1, parking: null, parkingSpaces: null,
  tenure: "leasehold",
  thumb: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200&q=80&auto=format&fit=crop",
};

const noop = () => {};
const noopAsync = async () => {};

export const MarketingDealRoom = () => {
  const deal = React.useMemo(buildDeal, []);
  const openTasks = React.useMemo(buildOpenTasks, []);

  return (
    <section
      id="the-deal-room"
      className="relative"
      style={{ background: "#FFFFFF" }}
      data-testid="landing-deal-room"
    >
      {/* Section framing — subtle top divider, ambient tint */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent 0%, rgba(15,23,42,0.08) 50%, transparent 100%)" }}
      />
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-16 lg:pb-24">
        {/* Copy block */}
        <div className="max-w-[720px]">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-violet-700">
            The Deal Room
          </p>
          <h2 className="font-display text-[32px] sm:text-[40px] lg:text-[52px] font-bold tracking-[-0.028em] text-slate-900 leading-[1.02] mt-4">
            One transaction.<br />
            Every party.<br />
            <span
              style={{
                background: "linear-gradient(90deg, #4F46E5 0%, #6D28D9 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              One clear view.
            </span>
          </h2>
          <p className="text-[14px] sm:text-[15.5px] text-slate-600 leading-relaxed mt-5 max-w-[560px]">
            The transaction, its people, its progress, its actions and its outstanding issues —
            organised together so the next step is always clear.
          </p>
        </div>

        {/* Real product composition — 35% copy / 65% product on
            desktop. Intercept card clicks so we don't fire modals
            or navigate on the marketing page. */}
        <div
          className="mt-10 lg:mt-14 grid grid-cols-1 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] gap-6 lg:gap-10 items-start"
          onClickCapture={(e) => {
            const isCta = e.target && e.target.closest && e.target.closest("[data-testid^='property-cta-']");
            if (isCta) return; /* let CTA behave (button no-op / Link disabled) */
            /* card body click → no-op on marketing */
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {/* Left column — property/transaction identity */}
          <div className="min-w-0">
            <div
              className="rounded-2xl bg-white ring-1 ring-slate-200/70 p-4 sm:p-5"
              style={{ boxShadow: "0 24px 60px -32px rgba(15,23,42,0.18), 0 6px 16px -10px rgba(15,23,42,0.06)" }}
              data-testid="landing-deal-room-identity"
            >
              <div className="max-w-full">
                <PilotPropertyCard p={HERO_PROPERTY} />
              </div>
            </div>
          </div>

          {/* Right column — real operational primitives */}
          <div className="min-w-0 space-y-5">
            <TransactionMomentum deal={deal} openTasks={openTasks} />

            <CriticalPathToExchange deal={deal} openTasks={openTasks} />

            <div
              className="rounded-2xl bg-white ring-1 ring-slate-200/70 p-5 lg:p-6"
              data-testid="landing-deal-room-participants"
            >
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500">
                    Participants
                  </p>
                  <h3 className="font-display text-[18px] font-bold tracking-tight text-slate-900 mt-0.5">
                    Every party in one place
                  </h3>
                </div>
              </div>
              <HeroParticipantsPanel
                participants={PARTICIPANTS}
                onAdd={noopAsync}
                onJumpToTab={noop}
                disabled={true}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketingDealRoom;
